import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ChartLineUp, Trophy, XCircle, Hourglass, TrendUp, CurrencyDollar,
  Sparkle, ArrowLeft, CheckCircle, Prohibit, CircleDashed, FunnelSimple, X as XIcon, Phone
} from "@phosphor-icons/react";
import client from "../lib/api";
import AnalyticsTrendChart from "../components/AnalyticsTrendChart";
import BrokerRelationshipsCard from "../components/BrokerRelationshipsCard";
import BrokerBracketsCard from "../components/BrokerBracketsCard";
import NegotiationReviewsCard from "../components/NegotiationReviewsCard";


const fmtMoney = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const fmtPerMi = (n) => `$${(n || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
};


export default function LoadBoardAnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");  // all | pending | won | lost
  const [brokerFilter, setBrokerFilter] = useState("");  // "" = all brokers
  const [brokerOptions, setBrokerOptions] = useState([]);  // populated from analytics/brokers

  // Load broker list once for the filter dropdown
  useEffect(() => {
    client.get("/loadboard/analytics/brokers?limit=100")
      .then((r) => setBrokerOptions(r.data.top_brokers || []))
      .catch(() => setBrokerOptions([]));
  }, [bids.length]);   // Refresh dropdown when new bids are recorded

  const reload = async () => {
    setLoading(true);
    try {
      const params = brokerFilter ? `?broker_company=${encodeURIComponent(brokerFilter)}` : "";
      const [a, b] = await Promise.all([
        client.get(`/loadboard/analytics${params}`),
        client.get(`/loadboard/bids${params}`),
      ]);
      setAnalytics(a.data);
      setBids(b.data.bids || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [brokerFilter]);

  const filteredBids = useMemo(() => {
    if (filter === "all") return bids;
    if (filter === "pending") return bids.filter((b) => b.status === "sent");
    if (filter === "won") return bids.filter((b) => b.status === "won");
    if (filter === "lost") return bids.filter((b) => b.status === "lost");
    if (filter === "auto") return bids.filter((b) => b.status === "closed_no_response");
    return bids;
  }, [bids, filter]);

  const markWon = async (bidId, finalRate) => {
    try {
      await client.post(`/loadboard/bids/${bidId}/mark-won`, {
        final_rate: finalRate ? parseFloat(finalRate) : null,
      });
      toast.success("Bid marked won 🎉");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const markLost = async (bidId) => {
    try {
      await client.post(`/loadboard/bids/${bidId}/mark-lost`);
      toast.info("Marked lost. It happens — next one's yours.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="loadboard-analytics-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link
            to="/app/loadboard"
            className="text-xs text-neutral-500 hover:text-white inline-flex items-center gap-1 mb-2"
            data-testid="analytics-back-to-loadboard"
          >
            <ArrowLeft size={12} /> Back to Load Board
          </Link>
          <div className="flex items-center gap-2 mb-1">
            <ChartLineUp size={22} className="text-[#FF5A1F]" weight="bold" />
            <div className="overline text-[#FF5A1F]">LOAD BOARD ANALYTICS</div>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">
            Your bid performance
          </h1>
          <p className="text-neutral-400 text-sm mt-2 max-w-xl">
            Track your win rate against the loads you bid on. The more you close the loop
            (Mark Won / Mark Lost), the sharper the AI Rate Negotiator gets.
          </p>
        </div>
      </div>

      {/* Broker filter — pilots can drill into a specific broker's performance */}
      {brokerOptions.length > 0 && (
        <div className="card-surface p-4 flex items-center gap-3 flex-wrap" data-testid="broker-filter-bar">
          <FunnelSimple size={14} className="text-neutral-500 shrink-0" />
          <label htmlFor="broker-filter-select" className="text-xs text-neutral-400 shrink-0">
            Filter by broker:
          </label>
          <select
            id="broker-filter-select"
            value={brokerFilter}
            onChange={(e) => setBrokerFilter(e.target.value)}
            data-testid="broker-filter-select"
            className="flex-1 min-w-[220px] max-w-md bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A1F] outline-none"
          >
            <option value="">All brokers ({brokerOptions.length})</option>
            {brokerOptions.map((b) => (
              <option key={b.broker_company} value={b.broker_company}>
                {b.broker_company}
                {b.win_rate_pct != null && ` — ${b.win_rate_pct}% win rate`}
                {" · "}{b.total_bids} bid{b.total_bids === 1 ? "" : "s"}
              </option>
            ))}
          </select>
          {brokerFilter && (
            <button
              onClick={() => setBrokerFilter("")}
              data-testid="broker-filter-clear"
              className="inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white transition-colors px-2 py-1 rounded-md hover:bg-white/5"
            >
              <XIcon size={12} /> Clear
            </button>
          )}
        </div>
      )}

      {/* Stats grid */}
      {loading ? (
        <div className="card-surface p-8 text-center text-neutral-500 text-sm" data-testid="analytics-loading">
          Loading…
        </div>
      ) : analytics ? (
        <>
          {analytics.hot_streak && (
            <div data-testid="hot-streak-badge" className="flex items-center gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
              <span className="text-xl">🔥</span>
              <div>
                <div className="text-sm font-bold text-amber-300">Hot streak — {analytics.wins_this_week} wins this week</div>
                <div className="text-[11px] text-amber-500/80">3+ booked loads in one week. Keep it rolling!</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="analytics-stats-grid">
            <StatCard icon={<Sparkle size={13} weight="fill" />} label="Total bids" value={analytics.total_bids} />
            <StatCard
              icon={<Trophy size={13} weight="fill" />}
              label="Won"
              value={analytics.won}
              tone="good"
              hint={(() => {
                const pw = bids.filter((b) => b.channel === "phone" && b.status === "won").length;
                return pw > 0 ? `${pw} booked by phone 📞` : undefined;
              })()}
            />
            <StatCard icon={<XCircle size={13} />} label="Lost" value={analytics.lost} tone="bad" />
            <StatCard icon={<Hourglass size={13} />} label="Pending close" value={analytics.pending} tone="info" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatCard
              icon={<TrendUp size={13} weight="fill" />}
              label="Win rate"
              value={`${analytics.win_rate_pct}%`}
              hint={`${analytics.won} won out of ${analytics.won + analytics.lost} closed`}
              tone="money"
              size="lg"
            />
            <StatCard
              icon={<CurrencyDollar size={13} />}
              label="Avg $/mi bid"
              value={fmtPerMi(analytics.avg_bid_per_mile)}
              hint={`vs. ${fmtPerMi(analytics.avg_posted_per_mile)} avg posted`}
              size="lg"
            />
            <StatCard
              icon={<Trophy size={13} weight="fill" />}
              label="Avg $/mi won"
              value={fmtPerMi(analytics.avg_won_per_mile)}
              hint={analytics.avg_won_per_mile > analytics.avg_posted_per_mile
                ? `+${fmtPerMi(analytics.avg_won_per_mile - analytics.avg_posted_per_mile)} above posted`
                : "close to posted rate"}
              tone={analytics.avg_won_per_mile > analytics.avg_posted_per_mile ? "good" : "default"}
              size="lg"
            />
          </div>

          {/* Phone vs Email — which channel closes more loads */}
          {analytics.channels?.phone?.sent > 0 && (
            <PhoneVsEmailCard channels={analytics.channels} />
          )}

          {/* Best Week Leaderboard — fleet competes on bookings */}
          <BestWeekLeaderboardCard />

          {/* Streak Leaderboard — longest active hot streak holds the flame */}
          <StreakLeaderboardCard />

          {/* 12-week trend chart — auto-hides when no bid history yet */}
          <AnalyticsTrendChart brokerFilter={brokerFilter} />

          {/* Top broker relationships — global aggregate. Hides when filtered
              to a specific broker because it becomes redundant (the stats grid
              + bid history already scope everything to that broker). */}
          {!brokerFilter && <BrokerRelationshipsCard />}

          {/* Broker Negotiation Brackets — the AI Negotiator's floor + ceiling
              math surfaced per broker. Same reason to hide when filtered — the
              draft-modal badges already surface this bracket for a single broker. */}
          {!brokerFilter && <BrokerBracketsCard />}

          {/* AI Negotiation Reviews — GPT-5.2 self-review of every closed bid.
              Hides when the analytics page is filtered (drill-down surfaces
              already show the per-bid outcome inline). */}
          {!brokerFilter && <NegotiationReviewsCard />}
        </>
      ) : null}

      {/* Bids table */}
      <div className="card-surface p-0 overflow-hidden">
        <div className="p-5 border-b border-white/5 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-white text-base">
              Bid history
              {brokerFilter && (
                <span className="text-[#FF5A1F] font-normal ml-2 text-sm" data-testid="broker-filter-badge">
                  · {brokerFilter}
                </span>
              )}
            </h3>
            <div className="text-xs text-neutral-500 mt-0.5">
              {filteredBids.length} {filter === "all" ? "total" : filter} bid{filteredBids.length === 1 ? "" : "s"}
              {brokerFilter && " with this broker"}
            </div>
          </div>
          <div className="flex items-center gap-1 bg-white/[0.02] rounded-full p-1" data-testid="bids-filter-tabs">
            {[
              { key: "all", label: "All" },
              { key: "pending", label: "Pending" },
              { key: "won", label: "Won" },
              { key: "lost", label: "Lost" },
              { key: "auto", label: "Auto-closed" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`bids-filter-${f.key}`}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  filter === f.key
                    ? "bg-white/10 text-white"
                    : "text-neutral-500 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {filteredBids.length === 0 ? (
          <div className="p-10 text-center" data-testid="bids-empty">
            <CircleDashed size={32} className="text-neutral-600 mx-auto mb-2" />
            <div className="text-sm text-neutral-400">
              {filter === "all"
                ? "No bids yet. Head to the Load Board and hit \"Draft Bid\" on a match to get started."
                : `No ${filter} bids yet.`}
            </div>
          </div>
        ) : (
          <div className="divide-y divide-white/5" data-testid="bids-list">
            {filteredBids.map((b) => (
              <BidRow key={b.id} bid={b} onMarkWon={markWon} onMarkLost={markLost} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function StatCard({ icon, label, value, hint, tone = "default", size = "md" }) {
  const toneMap = {
    default: "text-white",
    good: "text-emerald-400",
    bad: "text-red-400",
    info: "text-[#007AFF]",
    money: "text-[#FF5A1F]",
  };
  const sizeMap = { md: "text-2xl", lg: "text-3xl" };
  return (
    <div className="card-surface p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
        {icon}
        {label}
      </div>
      <div className={`font-display ${sizeMap[size]} font-semibold tracking-tight ${toneMap[tone]}`}>{value}</div>
      {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}


function PhoneVsEmailCard({ channels }) {
  const phone = channels.phone;
  const email = channels.email;
  const bothClosed = phone.win_rate_pct !== null && email.win_rate_pct !== null;
  const winner = bothClosed
    ? (phone.win_rate_pct === email.win_rate_pct ? null : (phone.win_rate_pct > email.win_rate_pct ? "phone" : "email"))
    : null;
  const Col = ({ name, icon, stats, isWinner }) => (
    <div className={`flex-1 rounded-xl border p-4 ${isWinner ? "border-emerald-500/40 bg-emerald-500/[0.06]" : "border-white/10 bg-white/[0.02]"}`} data-testid={`channel-col-${name}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs uppercase tracking-widest text-neutral-400 font-bold">{name}</span>
        {isWinner && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-bold text-emerald-300" data-testid={`channel-winner-${name}`}>
            <Trophy size={10} weight="fill" /> WINNER
          </span>
        )}
      </div>
      <div className="font-display text-2xl font-semibold text-white">
        {stats.win_rate_pct === null ? "—" : `${stats.win_rate_pct}%`}
        <span className="text-xs font-normal text-neutral-500 ml-1.5">win rate</span>
      </div>
      <div className="text-xs text-neutral-500 mt-1">
        {stats.won} won · {stats.lost} lost · {stats.sent} total
      </div>
    </div>
  );
  return (
    <div className="card-surface p-5" data-testid="phone-vs-email-card">
      <div className="flex items-center gap-2 mb-3">
        <Phone size={14} weight="fill" className="text-[#FF5A1F]" />
        <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Phone vs Email</span>
        <span className="text-[11px] text-neutral-600 ml-auto">which channel books more loads</span>
      </div>
      <div className="flex gap-3 flex-col sm:flex-row">
        <Col name="phone" icon={<Phone size={14} weight="fill" className="text-sky-300" />} stats={phone} isWinner={winner === "phone"} />
        <Col name="email" icon={<Sparkle size={14} weight="fill" className="text-[#FF5A1F]" />} stats={email} isWinner={winner === "email"} />
      </div>
    </div>
  );
}


function BestWeekLeaderboardCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    client.get("/loadboard/best-week-leaderboard").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || !data.leaderboard?.length) return null;
  return (
    <div className="card-surface p-5" data-testid="best-week-leaderboard-card">
      <div className="flex items-center gap-2 mb-3">
        <Trophy size={15} weight="fill" className="text-yellow-400" />
        <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Best Week Leaderboard</span>
        <span className="text-[11px] text-neutral-600">most loads booked in a single week</span>
        {data.your_rank && data.your_rank > 10 && (
          <span className="ml-auto text-[11px] text-neutral-400">Your rank: #{data.your_rank}</span>
        )}
      </div>
      <div className="space-y-1.5">
        {data.leaderboard.map((r) => (
          <div
            key={r.rank}
            className={`flex items-center gap-3 rounded-lg px-3 py-2 border ${
              r.is_you ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/[0.08]" : "border-white/5 bg-white/[0.02]"
            }`}
            data-testid={`best-week-row-${r.rank}`}
          >
            <span className={`w-6 text-center font-display font-bold text-sm ${
              r.rank === 1 ? "text-yellow-300" : r.rank === 2 ? "text-neutral-300" : r.rank === 3 ? "text-amber-600" : "text-neutral-500"
            }`}>
              {r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `#${r.rank}`}
            </span>
            <span className={`text-sm font-medium flex-1 min-w-0 truncate ${r.is_you ? "text-[#FF9764]" : "text-white"}`}>
              {r.name}
            </span>
            <span className="text-sm font-semibold text-emerald-300">{r.booked} booked</span>
            {r.week && <span className="text-[11px] text-neutral-500 w-20 text-right">{r.week}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}


function StreakLeaderboardCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    client.get("/loadboard/streak-leaderboard").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || !data.leaderboard?.length) return null;
  return (
    <div className="card-surface p-5" data-testid="streak-leaderboard-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm">🔥</span>
        <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Streak Leaderboard</span>
        <span className="text-[11px] text-neutral-600">3+ wins weekly · one ❄️ grace week per month</span>
        {data.your_rank && data.your_rank > 10 && (
          <span className="ml-auto text-[11px] text-neutral-400">Your rank: #{data.your_rank}</span>
        )}
      </div>
      <div className="space-y-1.5">
        {data.leaderboard.map((r) => (
          <div
            key={r.rank}
            className={`flex items-center gap-3 rounded-lg px-3 py-2 border ${
              r.is_you ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/[0.08]" : "border-white/5 bg-white/[0.02]"
            }`}
            data-testid={`streak-row-${r.rank}`}
          >
            <span className={`w-6 text-center font-display font-bold text-sm ${
              r.rank === 1 ? "text-amber-300" : "text-neutral-500"
            }`}>
              {r.rank === 1 ? "🔥" : `#${r.rank}`}
            </span>
            <span className={`text-sm font-medium flex-1 min-w-0 truncate ${r.is_you ? "text-[#FF9764]" : "text-white"}`}>
              {r.name}
            </span>
            <span className="text-sm font-semibold text-amber-300">
              {r.streak_weeks} {r.streak_weeks === 1 ? "week" : "weeks"}
            </span>
            {r.freezes_used > 0 && (
              <span className="text-[11px] text-sky-300" title="Grace week used" data-testid={`streak-freeze-${r.rank}`}>
                ❄️ {r.freezes_used}
              </span>
            )}
            <span className="text-[11px] text-neutral-500 w-24 text-right">
              {r.wins_this_week > 0 ? `${r.wins_this_week} wins this wk` : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


function BidRow({ bid, onMarkWon, onMarkLost }) {
  const [finalRate, setFinalRate] = useState("");
  const status = bid.status || "sent";
  const badgeStyle = {
    sent: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    won: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    lost: "bg-red-500/10 text-red-400 border-red-500/20",
    closed_no_response: "bg-neutral-500/10 text-neutral-400 border-neutral-500/25",
  }[status] || "";
  const badgeLabel = {
    sent: "PENDING", won: "WON", lost: "LOST",
    closed_no_response: "AUTO-CLOSED",
  }[status] || status.toUpperCase();

  return (
    <div className="p-5" data-testid={`bid-row-${bid.id.slice(0, 8)}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wider ${badgeStyle}`}
              title={status === "closed_no_response" ? "Auto-closed by system: no broker response after 7 days" : undefined}
            >
              {status === "won" && <CheckCircle size={10} weight="fill" />}
              {status === "lost" && <Prohibit size={10} weight="fill" />}
              {status === "sent" && <Hourglass size={10} />}
              {status === "closed_no_response" && <CircleDashed size={10} />}
              {badgeLabel}
            </span>
            {bid.channel === "phone" && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-sky-300"
                data-testid={`bid-phone-badge-${bid.id.slice(0, 8)}`}
                title="You called the broker on this load"
              >
                <Phone size={10} weight="fill" /> PHONE
              </span>
            )}
            <span className="text-white font-medium">{bid.origin} → {bid.destination}</span>
          </div>
          <div className="text-xs text-neutral-500">
            {bid.broker_company} · {bid.miles?.toLocaleString()}mi · {bid.equipment} · {fmtRelative(bid.created_at)}
          </div>
        </div>

        <div className="flex items-center gap-6 text-right">
          <div>
            <div className="text-[10px] text-neutral-500 tracking-widest">POSTED</div>
            <div className="font-mono text-sm text-neutral-300 tabular-nums">{fmtMoney(bid.posted_rate)}</div>
          </div>
          <div>
            <div className="text-[10px] text-[#FF5A1F] tracking-widest">BID</div>
            <div className="font-mono text-sm text-[#FF5A1F] font-semibold tabular-nums">{fmtMoney(bid.bid_amount)}</div>
          </div>
          {bid.final_rate && (
            <div>
              <div className="text-[10px] text-emerald-400 tracking-widest">WON @</div>
              <div className="font-mono text-sm text-emerald-400 font-semibold tabular-nums">{fmtMoney(bid.final_rate)}</div>
            </div>
          )}
        </div>
      </div>

      {status === "sent" && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-neutral-500">Close the loop:</span>
          <input
            type="number"
            step="10"
            placeholder="Final $"
            value={finalRate}
            onChange={(e) => setFinalRate(e.target.value)}
            data-testid={`bid-final-rate-input-${bid.id.slice(0, 8)}`}
            className="w-24 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-white focus:border-emerald-400 outline-none"
          />
          <button
            onClick={() => onMarkWon(bid.id, finalRate || bid.bid_amount)}
            data-testid={`bid-mark-won-${bid.id.slice(0, 8)}`}
            className="text-xs bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-full px-3 py-1 transition-colors"
          >
            Won 🏆
          </button>
          <button
            onClick={() => onMarkLost(bid.id)}
            data-testid={`bid-mark-lost-${bid.id.slice(0, 8)}`}
            className="text-xs text-neutral-400 hover:text-white transition-colors px-2"
          >
            Lost
          </button>
        </div>
      )}
    </div>
  );
}
